import heroBg from "../images/image 11.png";
import Navbar from "./Navbar";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function HeroSection() {
  const { t } = useTranslation();

  const fadeInUp = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 40 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: "easeOut", delay: 0.2 },
    },
  };

  return (
    <section className="relative flex flex-col w-full min-h-[65vh] md:min-h-[80vh] overflow-hidden bg-white pt-[104px] md:pt-[104px]">
      {/* Background Image */}
      <motion.img
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.5 }}
        src={heroBg}
        alt="Hero Background"
        className="absolute inset-0 object-cover w-full h-full"
      />

      <div className="absolute inset-0 bg-gray-50 opacity-30" />

      {/* Navbar */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-50 w-full"
      >
        <Navbar />
      </motion.div>

      {/* Hero Body — Two Columns */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="w-full max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-0">
          <div className="flex flex-col-reverse md:flex-row items-center justify-between gap-10 md:gap-6">
            {/* LEFT — Text Content */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.2 }}
              className="flex flex-col items-start text-left max-w-[620px] w-full"
            >
              <motion.h1
                variants={fadeInUp}
                className="mb-2 font-black leading-tight font-montserrat text-dark"
                style={{ fontSize: "clamp(22px, 3vw, 38px)" }}
              >
                {t("Swiss Tax Return Filing —")}
              </motion.h1>

              <motion.h2
                variants={fadeInUp}
                className="mb-5 font-black font-montserrat text-2xl md:text-3xl lg:text-4xl 
  bg-gradient-to-r from-[#036EA4] via-[#06A7CA] to-[#FBBF24] 
  bg-clip-text text-transparent leading-tight"
              >
                {t("Simple, Guided, Done Right.")}
              </motion.h2>

              <motion.p
                variants={fadeInUp}
                className="mb-8 font-sans leading-relaxed text-text text-sm md:text-base max-w-[440px]"
              >
                {t(
                  "Answer a few questions in under 3 minutes. We'll review your case and guide you through a canton-specific tax filing process clear, documented, and human-checked.",
                )}
              </motion.p>

              <motion.div
                variants={fadeInUp}
                className="flex flex-col items-start gap-2.5"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    document
                      .getElementById("questionnaire")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="inline-flex items-center gap-2 bg-[#036EA4] hover:bg-[#005f8d] text-white font-poppins font-semibold px-5 py-2.5 rounded-btn transition-all duration-200"
                  style={{ fontSize: "15px" }}
                >
                  {t("Jetzt Fragebogen starten")}
                  <ArrowRight size={18} />
                </motion.button>

                <p className="font-sans text-xs tracking-wide text-[#475569]">
                  {t("Free · No obligation · Canton-specific")}
                </p>
              </motion.div>
            </motion.div>

            {/* RIGHT — Illustration */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.2 }}
              variants={fadeInRight}
              className="w-full md:w-[45%] flex items-center justify-center"
            >
              <img
                src="/images/heroBg.png"
                alt="Tax Filing Illustration"
                className="w-full max-w-[460px] h-auto object-contain drop-shadow-xl hidden md:block"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
